import { AppCtx, AppCtxI } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../../../Event'
import { toInteger } from 'lodash'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { EnhancedDataPageTemplate, TemplateProps } from '../EnhancedDataPageTemplate'
import EDInputText from '../../../Component/EnhancedData/EDInputText'
import { DataWithKey, TextFieldDefinition } from '../../../Component/EnhancedData/Types'
import { toMap } from '../../../Helper/util'
import { EnhancedDataValidationError } from '../../../Type/EnhancedData'
import { ED_FIELD_SPEC, emptyEnhancedDataValidationError } from '../../../Helper/EnhancedData/Constants'
import { hasValidationError } from '../../../Helper/EnhancedData/EnhancedDataUtils'
import { Button, If } from '@paytrace/pt-skel/Component'
import { CheckCircleIcon } from '../../../Component/Icons'
import { MultipleRadioInput, MultipleRadioInputOpt } from '../../../Component/EnhancedData/MultipleRadioInput'

const self = Symbol('EnhancedDataSettingsSection')
const HELP_URL = 'https://paytrace.atlassian.net/wiki/spaces/HD/pages/3964764227/Enhanced+Data+Page+fields'
const PAGE_TITLE: string = 'Enhanced Data Settings'

const fieldDefs: DataWithKey<TextFieldDefinition>[] = [
  {
    key: 'merchantTaxId',
    name: 'merchantTaxId',
    label: 'Merchant Tax ID',
    showRequired: false,
    tooltip: ED_FIELD_SPEC.settings.merchantTaxId.tooltip,
    maxLength: ED_FIELD_SPEC.settings.merchantTaxId.maxLength,
    minLength: ED_FIELD_SPEC.settings.merchantTaxId.minLength,
    pattern: ED_FIELD_SPEC.settings.merchantTaxId.pattern,
    errorMessage: ED_FIELD_SPEC.settings.merchantTaxId.errorMessage,
  },
]
const fieldDefMap: Record<string, DataWithKey<TextFieldDefinition>> = toMap(fieldDefs)

export function EnhancedDataSettingsSection (): React.JSX.Element {
  const templateProps: TemplateProps = {
    title: PAGE_TITLE,
    redirectUrl: '',
    helpUrl: HELP_URL,
    symbol: self,
  }

  const appContext = useContext<AppCtxI>(AppCtx)
  const merchantId: number = toInteger(appContext.merchant)

  const [responseFeedback, setResponseFeedback] = useState('')
  const [errorFeedback, setErrorFeedback] = useState('')

  const [merchantTaxId, setMerchantTaxId] = useState<string>()
  const [isMerchantTaxExempt, setIsMerchantTaxExempt] = useState<boolean>(false)
  const [savedMerchantTaxId, setSavedMerchantTaxId] = useState<string>()
  const [validationError, setValidationError] = useState<EnhancedDataValidationError>(emptyEnhancedDataValidationError)

  const handleMerchantTaxIdInputChange = useCallback((fieldName: string, fieldValue: any) => {
    setMerchantTaxId(fieldValue)
  }, [])

  const handleMerchantTaxExemptInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    const booleanValue: boolean = JSON.parse(value)
    setIsMerchantTaxExempt(booleanValue)
  }, [])

  const handleInputFieldError = useCallback((fieldName: string, errorMessage: string | null): void => {
    setValidationError(prev => {
      const updatedInputErrors = { ...prev.inputErrors }
      if (errorMessage) {
        updatedInputErrors[fieldName] = errorMessage
      } else {
        delete updatedInputErrors[fieldName]
      }
      return {
        ...prev,
        inputErrors: updatedInputErrors,
      }
    })
  }, [])

  // Get current settings data and update state appropriately.
  useEffect(() => {
    const handleEnhancedDataResponse = (response?: any) => {
      if (response?.response_message) {
        setMerchantTaxId(response.masked_merchant_tax_id)
        setSavedMerchantTaxId(response.masked_merchant_tax_id)
        setIsMerchantTaxExempt(response.is_merchant_tax_exempt)
      }
    }

    PubSub.sub(self, Event.UPDATE_ED_MERCHANT_TAX_ID_SETTING_RESPONSE, (response?: any) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (response?.response_message) {
        setResponseFeedback(response.response_message)
        setErrorFeedback('')
        handleEnhancedDataResponse(response)
      } else {
        const errorMessage = 'Error updating merchant Enhanced Data'
        setResponseFeedback('')
        setErrorFeedback(response?.data?.detail ?? errorMessage)
      }
    })
    PubSub.sub(self, Event.GET_ED_MERCHANT_TAX_ID_SETTING_RESPONSE, handleEnhancedDataResponse)

    PubSub.pub(Event.GET_ED_MERCHANT_TAX_ID_SETTING_REQUEST, { merchantId })
    return () => { PubSub.unsub(self) }
  }, [merchantId])

  const saveEnhancedDataSettings = () => {
    const payload: any = {
      merchant_id: merchantId,
      is_merchant_tax_exempt: isMerchantTaxExempt,
    }

    const hasTaxIdChanged = merchantTaxId !== savedMerchantTaxId
    if (hasTaxIdChanged) {
      payload.merchant_tax_id = merchantTaxId
    }

    if (payload.is_merchant_tax_exempt != null || payload.merchant_tax_id != null) {
      PubSub.pub(Event.UPDATE_ED_MERCHANT_TAX_ID_SETTING_REQUEST, {
        merchantId,
        payload,
      })
    }
  }

  const taxExemptInputOptions: MultipleRadioInputOpt<boolean>[] = [
    {
      label: <span className={'tax-exempt-option-label'}>Yes, this company <strong>is</strong> tax-exempt</span>,
      name: 'isTaxExempt',
      value: true,
    },
    {
      label: <span className={'tax-exempt-option-label'}>No, this company <strong>is not</strong> tax-exempt</span>,
      name: 'isTaxExempt',
      value: false,
    }]

  function CompanyTaxExemptSelection ({ value, changeHandler }: { value: boolean; changeHandler: React.ChangeEventHandler; }) {
    return (
      <div className={'company-tax-exempt-selection'}>
        <MultipleRadioInput<boolean>
            legendLabel='Is your company tax-exempt?'
            options={taxExemptInputOptions}
            value={value}
            changeHandler={changeHandler}
          />
      </div>
    )
  }

  const handleFormSubmission = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    e.stopPropagation()

    if (!hasValidationError(validationError)) {
      saveEnhancedDataSettings()
    }
  }

  return (
    <EnhancedDataPageTemplate { ...templateProps }>
      <If is={!!responseFeedback}>
        <div className='success feedback'>
          {responseFeedback}
        </div>
      </If>
      <If is={!!errorFeedback}>
        <div className='error feedback'>
          {errorFeedback}
        </div>
      </If>
      <form
        id="enhanced-data-settings-form"
        className="enhanced-data-settings-form"
        autoComplete="off"
        onSubmit={handleFormSubmission}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
            e.preventDefault()
          }
        }}
      >
        <EDInputText
          value={merchantTaxId || ''}
          fieldDefinition={fieldDefMap?.merchantTaxId}
          className={'enhanced-data-input'}
          onInputChange={handleMerchantTaxIdInputChange}
          onInputError={handleInputFieldError}
        />
        <CompanyTaxExemptSelection value={isMerchantTaxExempt} changeHandler={handleMerchantTaxExemptInputChange}/>
        <div>
          <Button className='save-enhanced-data-settings-btn' id='save-enhanced-data-settings-btn'>
            <CheckCircleIcon/>
            <span>Save</span>
          </Button>
        </div>
      </form>
    </EnhancedDataPageTemplate>
  )
}
